import React, { useEffect, useState } from 'react';
import { customerAPI, configAPI } from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../utils/i18n';
import { Search, Plus } from 'lucide-react';
import './StepStyles.css';

const Step6Customer = ({ formData, setFormData, errors = {} }) => {
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [mode, setMode] = useState('manual'); // Always 'manual' - removed customer selection
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [visaTypes, setVisaTypes] = useState([]);

  useEffect(() => {
    // Removed customer fetching - always manual entry
    fetchCountries();
    fetchVisaTypes();
  }, []);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const response = await customerAPI.getAll();
      // Ensure customers is always an array
      const customersData = response.data?.customers || response.customers || response.data || [];
      setCustomers(Array.isArray(customersData) ? customersData : []);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await configAPI.getCountries({ active: 'true' });
      const countriesData = response.data?.data || [];
      setCountries(Array.isArray(countriesData) ? countriesData : []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
      setCountries([]);
    }
  };

  const fetchVisaTypes = async () => {
    try {
      const response = await configAPI.getVisaTypes({ active: 'true' });
      const visaTypesData = response.data?.data || [];
      setVisaTypes(Array.isArray(visaTypesData) ? visaTypesData : []);
    } catch (error) {
      console.error('Failed to fetch visa types:', error);
      setVisaTypes([]);
    }
  };

  const handleCustomerSelect = (customer) => {
    setFormData((prev) => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.phone,
    }));
  };

  const handleManualInput = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      customerId: null, // Clear customer ID when manual input
    }));
  };

  const getLocalizedName = (item) => {
    if (!item) return '';
    if (language === 'ko') return item.nameKo || item.nameEn || item.nameVi;
    if (language === 'en') return item.nameEn || item.nameVi;
    return item.nameVi;
  };

  const filteredCustomers = Array.isArray(customers)
    ? customers.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.phone?.includes(searchTerm)
      )
    : [];

  const selectedCustomer = Array.isArray(customers)
    ? customers.find((c) => c.id === formData.customerId)
    : null;

  return (
    <div className="step-container">
      {/* Removed mode switch - always manual entry */}

      {/* Disabled customer selection mode */}
      {false ? (
        <>
          <div className="search-box">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              placeholder={t('searchByNamePhone')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          {loading ? (
            <div className="loading-small">{t('loading')}</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="empty-state-small">
              <p>{t('noCustomerFound')}</p>
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={() => setMode('manual')}
              >
                <Plus size={16} />
                {t('enterManuallyBtn')}
              </button>
            </div>
          ) : (
            <div className="customer-select-list">
              {filteredCustomers.map((customer) => (
                <button
                  key={customer.id}
                  type="button"
                  className={`customer-select-item ${
                    formData.customerId === customer.id ? 'selected' : ''
                  }`}
                  onClick={() => handleCustomerSelect(customer)}
                >
                  <div className="option-radio">
                    {formData.customerId === customer.id && <div className="radio-dot"></div>}
                  </div>
                  <div className="customer-select-content">
                    <div className="customer-select-name">{customer.name}</div>
                    <div className="customer-select-details">
                      {customer.phone}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedCustomer && (
            <div className="step-info success">
              ✓ {t('selected')}: <strong>{selectedCustomer.name}</strong>
              <br />
              {t('phone')}: {selectedCustomer.phone}
            </div>
          )}
        </>
      ) : (
        <div className="manual-input-form">
          <div className={`form-group ${errors.customerName ? 'has-error' : ''}`}>
            <label className="form-label">
              {t('fullNameLabel')} <span className="required">*</span>
            </label>
            <input
              type="text"
              value={formData.customerName || ''}
              onChange={(e) => handleManualInput('customerName', e.target.value)}
              placeholder={t('namePlaceholder')}
              className={errors.customerName ? 'input-error' : ''}
            />
            {errors.customerName && <span className="error-message">{errors.customerName}</span>}
          </div>

          <div className={`form-group ${errors.customerPhone ? 'has-error' : ''}`}>
            <label className="form-label">
              {t('phoneLabel')} <span className="required">*</span>
            </label>
            <input
              type="tel"
              value={formData.customerPhone || ''}
              onChange={(e) => handleManualInput('customerPhone', e.target.value)}
              placeholder={t('phonePlaceholder')}
              className={errors.customerPhone ? 'input-error' : ''}
            />
            {errors.customerPhone && <span className="error-message">{errors.customerPhone}</span>}
          </div>

          <div className={`form-group ${errors.customerCountry ? 'has-error' : ''}`}>
            <label className="form-label">
              {t('countryLabel')} <span className="required">*</span>
            </label>
            <select
              value={formData.customerCountry || ''}
              onChange={(e) => handleManualInput('customerCountry', e.target.value)}
              className={errors.customerCountry ? 'input-error' : ''}
            >
              <option value="">{t('selectCountry')}</option>
              {countries.map((country) => (
                <option key={country.id} value={country.code}>
                  {getLocalizedName(country)}
                </option>
              ))}
            </select>
            {errors.customerCountry && <span className="error-message">{errors.customerCountry}</span>}
          </div>

          <div className={`form-group ${errors.customerVisaType ? 'has-error' : ''}`}>
            <label className="form-label">
              {t('visaTypeLabel')} <span className="required">*</span>
            </label>
            <select
              value={formData.customerVisaType || ''}
              onChange={(e) => handleManualInput('customerVisaType', e.target.value)}
              className={errors.customerVisaType ? 'input-error' : ''}
            >
              <option value="">{t('selectVisaTypePlaceholder')}</option>
              {visaTypes.map((visaType) => (
                <option key={visaType.id} value={visaType.code}>
                  {getLocalizedName(visaType)}
                </option>
              ))}
            </select>
            {errors.customerVisaType && <span className="error-message">{errors.customerVisaType}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">{t('emailLabel')}</label>
            <input
              type="email"
              value={formData.customerEmail || ''}
              onChange={(e) => handleManualInput('customerEmail', e.target.value)}
              placeholder={t('emailPlaceholder')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">{t('idNumberLabel')}</label>
            <input
              type="text"
              value={formData.customerIdNumber || ''}
              onChange={(e) => handleManualInput('customerIdNumber', e.target.value)}
              placeholder={t('idPlaceholder')}
            />
          </div>
        </div>
      )}

      {(!formData.customerName || !formData.customerPhone || !formData.customerCountry || !formData.customerVisaType) && (
        <div className="step-info warning">
          ⚠️ {t('fillRequired')}
        </div>
      )}
    </div>
  );
};

export default Step6Customer;
